<script lang="ts">
	import Navbar from '../components/Navbar.svelte';
	import { push } from 'svelte-spa-router';
	import { appStore } from '../stores/appStore';
	import type { AppApiClient } from '../api_contracts/app.client';
	import { getTokenStorage } from '../utils/localStorage';

	let client: AppApiClient | null = $state(null);
	let token = $state('');

	// Subscribe to store
	appStore.subscribe((state) => {
		client = state.client;
		token = state.token || getTokenStorage() || '';
	});

	let formData = $state({
		name: '',
		dbType: 'postgres'
	});

	let loading = $state(false);
	let error = $state('');
	let success = $state(false);

	const dbTypes = [
		{ value: 'postgres', label: 'PostgreSQL', icon: '🐘', description: 'Popular open-source relational database' },
		{ value: 'mysql', label: 'MySQL', icon: '🐬', description: 'World\'s most popular open-source database' },
		{ value: 'mongodb', label: 'MongoDB', icon: '🍃', description: 'Document-oriented NoSQL database' }
	];

	async function handleSubmit(e: Event) {
		e.preventDefault();

		if (!client) {
			error = 'Client not initialized';
			return;
		}

		if (!token) {
			error = 'You must be logged in to create a database';
			push('/login');
			return;
		}

		loading = true;
		error = '';

		try {
			const result = await client.createDatabase({
				token,
				name: formData.name,
				dbType: formData.dbType
			});

			if (result.response.successFull) {
				success = true;
				// Redirect to databases page after 2 seconds
				setTimeout(() => {
					push('/databases');
				}, 2000);
			} else {
				error = result.response.reason || 'Failed to create database';
			}
		} catch (e: any) {
			error = e.message || 'An error occurred while creating the database';
			console.error('Database creation error:', e);
		} finally {
			loading = false;
		}
	}

	function handleCancel() {
		push('/databases');
	}
</script>

<Navbar />

<div class="min-h-screen bg-gray-50 dark:bg-gray-900">
	<div class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
		<div class="mb-8">
			<button
				onclick={handleCancel}
				class="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 mb-4 inline-flex items-center"
			>
				<svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
				</svg>
				Back to Databases
			</button>
			<h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">Create New Database</h1>
			<p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
				Deploy a new database instance with one click
			</p>
		</div>

		{#if success}
			<div
				class="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 text-center"
			>
				<svg
					class="w-16 h-16 text-green-500 mx-auto mb-4"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
					/>
				</svg>
				<h2 class="text-2xl font-semibold text-green-800 dark:text-green-400 mb-2">
					Database Created!
				</h2>
				<p class="text-green-700 dark:text-green-300">
					Your database is being provisioned. Redirecting to databases...
				</p>
			</div>
		{:else}
			{#if error}
				<div
					class="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded"
				>
					{error}
				</div>
			{/if}

			<form
				onsubmit={handleSubmit}
				class="bg-white dark:bg-gray-800 shadow-sm rounded-lg border border-gray-200 dark:border-gray-700"
			>
				<div class="p-6 space-y-6">
					<!-- Database Name -->
					<div>
						<label
							for="name"
							class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
						>
							Database Name <span class="text-red-500">*</span>
						</label>
						<input
							type="text"
							id="name"
							bind:value={formData.name}
							required
							placeholder="my-app-database"
							pattern="[a-z0-9-]+"
							maxlength="63"
							class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-gray-100"
						/>
						<p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
							Only lowercase letters, numbers, and hyphens (max 63 characters)
						</p>
					</div>

					<!-- Database Type -->
					<div>
						<label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
							Database Type <span class="text-red-500">*</span>
						</label>
						<div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
							{#each dbTypes as type}
								<label
									class="relative flex cursor-pointer rounded-lg border {formData.dbType ===
									type.value
										? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20'
										: 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700'} p-4 hover:border-indigo-500 dark:hover:border-indigo-400 transition"
								>
									<input
										type="radio"
										name="dbType"
										value={type.value}
										bind:group={formData.dbType}
										class="sr-only"
									/>
									<div class="flex flex-col flex-1">
										<div class="flex items-center justify-between mb-2">
											<span class="text-2xl">{type.icon}</span>
											{#if formData.dbType === type.value}
												<svg
													class="h-5 w-5 text-indigo-600"
													fill="currentColor"
													viewBox="0 0 20 20"
												>
													<path
														fill-rule="evenodd"
														d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
														clip-rule="evenodd"
													/>
												</svg>
											{/if}
										</div>
										<span
											class="block text-sm font-medium {formData.dbType === type.value
												? 'text-indigo-900 dark:text-indigo-300'
												: 'text-gray-900 dark:text-gray-100'}"
										>
											{type.label}
										</span>
										<span
											class="mt-1 text-xs {formData.dbType === type.value
												? 'text-indigo-700 dark:text-indigo-400'
												: 'text-gray-500 dark:text-gray-400'}"
										>
											{type.description}
										</span>
									</div>
								</label>
							{/each}
						</div>
					</div>

					<!-- Information Box -->
					<div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
						<div class="flex">
							<svg
								class="h-5 w-5 text-blue-400 mr-3 mt-0.5"
								fill="currentColor"
								viewBox="0 0 20 20"
							>
								<path
									fill-rule="evenodd"
									d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
									clip-rule="evenodd"
								/>
							</svg>
							<div class="text-sm text-blue-700 dark:text-blue-300">
								<p class="font-medium mb-1">What happens next?</p>
								<ul class="list-disc list-inside space-y-1 text-xs">
									<li>A new database instance will be created with secure credentials</li>
									<li>The database will be accessible within minutes</li>
									<li>You'll receive connection details to integrate with your application</li>
									<li>Automatic backups will be configured for data safety</li>
								</ul>
							</div>
						</div>
					</div>
				</div>

				<!-- Form Actions -->
				<div
					class="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 rounded-b-lg flex justify-end gap-3"
				>
					<button
						type="button"
						onclick={handleCancel}
						disabled={loading}
						class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
					>
						Cancel
					</button>
					<button
						type="submit"
						disabled={loading}
						class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
					>
						{#if loading}
							<svg
								class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
								fill="none"
								viewBox="0 0 24 24"
							>
								<circle
									class="opacity-25"
									cx="12"
									cy="12"
									r="10"
									stroke="currentColor"
									stroke-width="4"
								/>
								<path
									class="opacity-75"
									fill="currentColor"
									d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
								/>
							</svg>
							Creating...
						{:else}
							<svg class="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M12 4v16m8-8H4"
								/>
							</svg>
							Create Database
						{/if}
					</button>
				</div>
			</form>
		{/if}
	</div>
</div>
